import 'src/styles/index.scss';

import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import dayjs from 'dayjs';
import duration from 'dayjs/plugin/duration';
import React, { useContext, useEffect, useState } from 'react';
import { Helmet } from 'react-helmet';
import { hot } from 'react-hot-loader/root';
import { useLocation } from 'react-router-dom';
import { ToastProvider } from 'react-toast-notifications';
import { CSSTransition } from 'react-transition-group';
import ActionPanel from 'src/components/action-panel';
import ActionProgress from 'src/components/action-progress';
import Loader from 'src/components/loader';
import ModalObserver from 'src/components/ModalObserver';
import YBugFloatingButton from 'src/components/yBugFloatingButton';
import FeaturesContext from 'src/context/FeaturesContext';
import UserContext from 'src/context/UserContext';
import { YBugProvider } from 'src/context/YBugContext';
import { PanelVisibilityProvider } from 'src/hooks/usePanelVisibility';
import useActionPanelStore from 'src/stores/useActionPanelStore';
import { api } from 'src/utils/api';
import Config from 'src/utils/config';
import config from 'src/utils/config';
import { API_RESOURCES } from 'src/utils/constants';
import { LOCALSTORAGE_KEYS } from 'src/utils/constants';

import ActionProvider from './components/action-provider';
import InstallPrompt from './components/install-prompt';
import SentryErrorBoundary from './components/sentry-error-boundary';
import RouterComponent from './routes';
import { getFeatures, getSession } from './utils/auth';
import Sentry, { init as initSentry } from './utils/sentry';

const client = new QueryClient({});

initSentry({
  dsn: config.sentryDsn,
  environment: config.environment,
  release: config.commit,
  sampleRate: config.sentrySampleRate,
});

dayjs.extend(duration);

const App = () => {
  const location = useLocation();
  const [installPrompt, setInstallPrompt] = useState(false);
  const { user, setUser } = useContext(UserContext);
  const { setFeatures } = useContext(FeaturesContext);
  const [isLoading, setIsLoading] = useState(true);
  const { panelStack } = useActionPanelStore();
  const isActionPanelOpen = panelStack.length > 0;

  useEffect(() => {
    if (user) {
      Sentry.configureScope(scope => {
        scope.setUser({
          id: user.uri,
          email: user.username,
          name: user.name,
        });
      });
    }

    const initialize = event => {
      event.preventDefault();

      let hidePrompt = false;
      const travelerPage = location.pathname.startsWith('/traveler');

      try {
        const storageValue = window.localStorage.getItem('installPrompt');
        hidePrompt = storageValue === 'hidden';
      } catch (_) {
        // ignore
      }

      if (hidePrompt || travelerPage) {
        return;
      }
      setInstallPrompt(event);
    };

    window.addEventListener('beforeinstallprompt', initialize);
    return () => {
      window.removeEventListener('beforeinstallprompt', initialize);
    };
  }, []);

  const getSettings = async () => {
    try {
      const { resources } = await api.get(`${API_RESOURCES.BUREAU_SETTINGS}/`).json();
      localStorage.setItem(
        LOCALSTORAGE_KEYS.SCAN_MODE,
        JSON.stringify(resources[0].barcode_default_format)
      );
    } catch (error) {
      console.log(error);
    }
  };

  const getInitialData = async () => {
    let user = null;
    let features = [];

    try {
      user = await getSession();
      setUser(user);
    } catch (error) {
      Sentry.captureException(error);
    }

    if (user) {
      try {
        features = await getFeatures();
        setFeatures(features);
      } catch (error) {
        Sentry.captureException(error);
      }
    }
  };

  useEffect(() => {
    getInitialData().finally(() => setIsLoading(false));
    getSettings();
  }, []);

  const hideInstallPrompt = () => {
    setInstallPrompt(false);

    try {
      window.localStorage.setItem('installPrompt', 'hidden');
    } catch (_) {
      // ignore
    }
  };

  const promptToInstall = () => {
    installPrompt.prompt();
    installPrompt.userChoice.then(() => {
      hideInstallPrompt();
    });
  };

  if (isLoading) {
    return <Loader />;
  }

  return (
    // eslint-disable-next-line camelcase
    <YBugProvider yBugId={Config.yBugId} settings={{ hide_launcher: true }}>
      <Helmet>
        <title>Authentise QR</title>
      </Helmet>
      <SentryErrorBoundary userName={user?.name || ''} userEmail={user?.username || ''}>
        <PanelVisibilityProvider>
          <ToastProvider autoDismiss autoDismissTimeout={4000}>
            <QueryClientProvider client={client}>
              <ActionProvider>
                <RouterComponent user={user} />
                <ActionPanel />
              </ActionProvider>
              <ModalObserver>
                <YBugFloatingButton position={isActionPanelOpen ? 'right-bottom' : 'right'} />
              </ModalObserver>
            </QueryClientProvider>

            <QueryClientProvider client={client}>
              <ActionProgress user={user} />
            </QueryClientProvider>
          </ToastProvider>
        </PanelVisibilityProvider>

        <CSSTransition in={Boolean(installPrompt)} timeout={200} classNames='slide'>
          <InstallPrompt onConfirm={promptToInstall} onDismiss={hideInstallPrompt} />
        </CSSTransition>
      </SentryErrorBoundary>
    </YBugProvider>
  );
};

export default hot(App);
